import useApi from '../../../hooks/use-api';
import React, { Fragment, useMemo, useState } from 'react';
import useTable from '../../../hooks/useTable';
import {
  AceClientTypes,
  AceClientTypesLabels,
  ButtonSize,
  ButtonStyle,
  ConsumersTableFilters,
  Counter,
  Consumer,
  IconsTypes,
  IndexPeriodsRule,
  IndexPeriodsRules,
  MessageStyle,
  RowRenderProps,
  RulesTypes,
  SORT_ORDER,
  SortSelectValue,
  UseAPIReturnType,
} from '../../../utils/props';
import PageContainer from '../../../components/PageContainer';
import useSearch from '../../../hooks/useSearch';
import PaginationPagesBar from '../../../components/Table/Pagination/PaginationPagesBar';
import pick from 'lodash/pick';
import Search from '../../../components/Table/Search';
import NumberOfResults from '../../../components/Table/Pagination/NumberOfResults';
import Button from '../../../components/Button/Button';
import Icon from '../../../components/Icon/Icon';
import { formatDisplayedValue, getFilename, getLabelName, isLogged } from '../../../utils/utils';
import LoadingOverlay from '../../../components/Spinner/LoadingOverlay';
import { useTranslation } from 'react-i18next';
import EditableCell from '../../../components/Table/EditableCell';
import Message from '../../../components/Message/Message';

export default function IndexPeriods() {
  const { t } = useTranslation('ns');
  const [currentlyEditing, setCurrentlyEditing] = useState<{
    rowId: number;
    field: 'interval-start' | 'interval-end';
  } | null>(null);
  const [loading, setLoading] = useState(false);
  
  const rules: UseAPIReturnType<IndexPeriodsRules> = useApi({
    path: `/invoice_period_rules`,
  });

  const {
    search,
    setSearch,
    sort,
    setSort,
    page,
    setPage,
    numberOfResultsToDisplay,
    setNumberOfResultsToDisplay,
    exporting,
    prepareQuery
  } = useSearch<Counter, undefined, SortSelectValue>({
    api: rules,
    defaultSort: {
      sortBy: 'joined',
      sortOrder: SORT_ORDER.ASC,
    },
  });

  const consumersApi: UseAPIReturnType<Consumer> = useApi({
    path: `/admin/counters`,
  });

  const {
    exportToCsv: consumersExport,
  } = useSearch<Consumer, ConsumersTableFilters, SortSelectValue>({
    api: consumersApi,
    defaultSort: {
      sortBy: 'joined',
      sortOrder: SORT_ORDER.DESC,
    },
    stopFetch: true
  });
 
  const updateRule = useApi({
    path: `/invoice_period_rule/${currentlyEditing?.rowId}`,
    callback: async () => {
      const query = prepareQuery();
      await rules.get(`?${query}`);
    },
    defaultSuccessMessage: t('interval-change-success'),
    defaultErrorMessage: t('interval-change-failure'),
  });

  const paginatedResponseMeta = useMemo(
    () => pick(rules?.data, ['items_no', 'current_page', 'items_per_page', 'total_pages']),
    [rules?.data]
  );

  const handleExport = () => {
    const filename = getFilename(t('export-indexes-sufix'), true, '.csv');
    consumersExport(filename);
  };

  const handleSingleExport = (id: number, city: string) => {
    const filename = getFilename(`${city}_${t(`export-indexes-sufix`)}`, true, '.csv');
    consumersExport(filename, id);
  }

  const editIndex = async ( id: number, field: 'interval-start' | 'interval-end', value: string) => {
    setLoading(true);

    const rule = rules.data?.items.find((r: IndexPeriodsRule) => r.id === id);
    
    if (!rule) {
      setLoading(false);
      return;
    }

    const payload = {
      start_day: field === 'interval-start' ? Number(value) : Number(rule.start_day),
      end_day: field === 'interval-end' ? Number(value) : Number(rule.end_day),
    };

    await updateRule.put(payload);

    setLoading(false);
    setCurrentlyEditing(null);
  };

  const { TableComponent } = useTable({
    columns: [
      {
        name: 'location',
        label: t('location'),
        className: 'whitespace-nowrap max-w-full min-w-1/5 w-1/5 p-3'
      },
      {
        name: 'ace_client_type',
        label: t('subscriber-type'),
        className: 'whitespace-nowrap max-w-full w-1/6 p-3'
      },
      {
        name: 'interval_start',
        label: t('interval-start'),
        className: 'whitespace-nowrap max-w-full w-1/4 p-3'
      },
      {
        name: 'interval-end',
        label: t('interval-end'),
        className: 'whitespace-nowrap max-w-full w-1/4 p-3'
      },
      {
        name: '',
        label: '',
        className: 'whitespace-nowrap max-w-full p-3'
      },
    ],
    items: rules.data?.items
      ?.filter((rule: IndexPeriodsRule) => rule.city.trim() !== '')
      .map((rule: IndexPeriodsRule) => ({
      data: rule,
      render: ({ CellComponent }: RowRenderProps) => (
        <Fragment>
          <CellComponent tooltipEnabled className="max-w-full min-w-1/5 w-1/5 max-h-[70px]">
            {rule.city}
          </CellComponent>
          <CellComponent tooltipEnabled className="max-w-[200px] w-1/6">
            <div className="whitespace-normal break-words">
              {AceClientTypesLabels[(AceClientTypes[rule.client_type] as keyof typeof AceClientTypesLabels)]}
            </div>
          </CellComponent>
          <CellComponent className="max-w-full w-1/4 max-h-[70px] flex gap-3">
            <EditableCell
              rowId={rule.id}
              field="interval-start"
              value={rule.start_day}
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              loading={loading}
              editIndex={editIndex}
            />
          </CellComponent>
          <CellComponent className="max-w-full w-1/4 max-h-[70px] flex gap-3">
            <EditableCell
              rowId={rule.id}
              field="interval-end"
              value={rule.end_day}
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              loading={loading}
              editIndex={editIndex}
            />
          </CellComponent>
          <CellComponent className="max-w-full relative whitespace-nowrap pl-3 pr-4 text-left sm:pr-3 cursor-pointer">
            <div
              className="text-blue-900 flex"
              onClick={() => handleSingleExport(rule.id, rule.city)}
            >
              <Icon
                icon={IconsTypes.Download}
                size="16"
                className="fill-blue-900 mr-1 mt-0.5 cursor-pointer w-4 h-4"
              />
              <div>
                {t('download')}
              </div>
            </div>
          </CellComponent>
        </Fragment>
      ),
    })),
    sortSettings: {
      sort,
      setSort,
    },
  });

  return (
    <>
      {isLogged() ? (
        <PageContainer className="flex flex-col gap-y-7">
          <div className="flex gap-5 items-center">
            <Search title={t('index-period')} search={search} setSearch={setSearch} />
            <Button
              size={ButtonSize.Large}
              variant={ButtonStyle.Secondary}
              title={t('export-csv')}
              loading={exporting}
              onClick={handleExport}
              >
              <Icon
                icon={IconsTypes.Download2}
                size="16"
                className="h-5 w-5 text-blue-900 fill-blue-900"
                aria-hidden="true"
                />
              {t('export-csv')}
            </Button>
          </div>
          <div className="flex justify-between gap-4">
            <NumberOfResults
              numberOfResultsToDisplay={numberOfResultsToDisplay}
              setNumberOfResultsToDisplay={setNumberOfResultsToDisplay}
              meta={paginatedResponseMeta}
              />
          </div>
          {updateRule.errors && (
            <Message type={MessageStyle.Error} className="mb-6" onCloseFunction={updateRule.closeMessages}>
              {updateRule.errors}
            </Message>
          )}
          {updateRule.successMessage && (
            <Message type={MessageStyle.Success} className="mb-6" onCloseFunction={updateRule.closeMessages}>
              {updateRule.successMessage}
            </Message>
          )}
          {TableComponent()}
          <PaginationPagesBar meta={paginatedResponseMeta} setPage={setPage} />
        </PageContainer>
      ) : (
        <LoadingOverlay />
      )}
    </>
  );
}
